/*
 * Person policies (audit Phase 3.3). Replaces wwwroot/js/PersonPolicy/List.js,
 * Create.js and Edit.js on the Phase 3.1 core. No jQuery.
 *
 * One screen: the account policies of a Domain -- password rules, lockout, the
 * login window and session expiry -- with create and edit dialogs over it.
 * This is the policy ADMIN screen. The policy card on Person Detail is
 * Person.PolicyAssign, a different surface that only picks one of these.
 *
 * The login window is stored in UTC and shown in local time, so both forms
 * carry data-mcp-utc-form and are serialized with serializeUtc.
 *
 * #PersonPolicyModal is INSIDE the list partial, so a write that redraws the
 * list has to let the dialog finish hiding first.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { all, one } from '../core/dom';
import { initTimeWindow, serializeUtc } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'PersonPolicyModal';
const CREATE_FORM = 'CreatePersonPolicyForm';
const EDIT_FORM = 'EditPersonPolicyForm';
const CREATE_ALERT = 'PersonPolicyCreateMessageContainer';
const EDIT_ALERT = 'PersonPolicyEditMessageContainer';

/** The Domain the list was rendered for (its own hidden field, not the rail). */
function listDomainId(): string {
    return (one('#PersonPolicyDomainId') as HTMLInputElement | null)?.value || '0';
}

/** The save button's icon swap; scoped to the modal, whose footer is outside the form. */
function saving(working: boolean): void {
    const modal = one(`#${MODAL}`);
    if (!modal) return;
    for (const node of Array.from(modal.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
    for (const node of Array.from(modal.querySelectorAll('.fa-spinner'))) node.classList.toggle('d-none', !working);
}

/**
 * Start the login-window controls from the values the server rendered: both
 * times empty means "no restriction", which is the checkbox ticked and the
 * inputs disabled so they serialize as absent.
 *
 * An enhancer, so it runs wherever either form lands. It used to be called
 * from the ajaxModal onSuccess of whichever handler opened the dialog, which
 * meant a form reached by any other path showed an unticked checkbox over two
 * empty, enabled time inputs -- a window of 00:00 to 00:00 waiting to be saved.
 */
export function enhancePolicyTimeWindow(root: ParentNode): void {
    if (!root.querySelector('#LoginWindowTimes')) return;
    initTimeWindow('LoginNoTimeWindow', '#LoginWindowTimes');
}

export const List = {
    /**
     * Open one policy. `false` suppresses the row's own default action, which
     * matters because the delete control is a link inside the same row.
     */
    RowClick(id: number | string): boolean {
        if (!(Number(id) > 0)) return false;
        void ajaxModal({
            modal: MODAL,
            // No `title:` -- Edit.cshtml owns the whole modal-dialog, and a
            // title would make showModal wrap a second dialog around it.
            url: `PersonPolicy/Edit/${id}`,
            formId: EDIT_FORM,
            focusId: 'Name',
            errorTitle: `Personal Policy ${id}`,
            errorMessage: 'Person Policy Edit failed to execute:',
        });
        return false;
    },

    /**
     * Re-render the list. The list is a PartialView, so a page reload would
     * land on a layout-less fragment; re-fetch the Domain route for the Domain
     * the list itself was rendered for.
     */
    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `PersonPolicy/Domain/${listDomainId()}`,
            alertTitle: 'Failed to reload policies',
        });
    },

    DisplayCreateModal(): void {
        void ajaxModal({
            modal: MODAL,
            url: `PersonPolicy/Create?domainId=${encodeURIComponent(listDomainId())}`,
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New Personal Policy',
            errorMessage: 'New Personal Policy View failed to execute:',
        });
    },

    DeleteRow(id: number | string): boolean {
        if (!(Number(id) > 0)) return false;
        void confirm({
            title: 'Delete Person Policy',
            html: 'Delete this Policy?<br/><br/>'
                + '<span class="text-danger"><strong>WARNING:</strong> anyone assigned to it falls back to '
                + 'default policy handling until they are reassigned.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `PersonPolicy/${id}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to delete policy',
                    onSuccess: () => {
                        toast('success', 'Policy deleted');
                        removeRow(id);
                    },
                    onFailure: failToast('Failed to delete policy'),
                    onError: (xhr, _status, error) => {
                        toast('danger', 'Failed to delete policy', xhr.statusText || error);
                        return true;
                    },
                });
            },
        });
        return false;
    },
};

export const Create = {
    /**
     * POST the create form as a JSON-envelope write. The form has no submit of
     * its own -- a native submit would GET-navigate and discard the entry --
     * and serializeUtc converts the data-mcp-utc-time inputs, because the
     * document-level submit hook that normally does that never fires here.
     */
    SaveClick(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: 'PersonPolicy/Create',
            method: 'POST',
            data: serializeUtc(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create policy',
            onSuccess: () => {
                toast('success', 'Policy created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create policy', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },
};

export const Edit = {
    DeletePolicy(): void {
        const policyId = (one('#PolicyId') as HTMLInputElement | null)?.value ?? '';
        if (!policyId) return;
        void confirm({
            title: 'Delete Person Policy',
            html: 'Delete this policy?<br/><br/>'
                + '<span class="text-danger"><strong>WARNING:</strong> this may change what any assigned '
                + 'people, applications or resources are allowed to do.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `PersonPolicy/${policyId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'An error occurred attempting to delete the policy',
                    onSuccess: () => {
                        toast('success', 'Policy deleted');
                        // Redraw rather than pluck the row out: the header
                        // counts and the empty state are rendered server-side.
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete policy'),
                });
            },
        });
    },

    /** PUT the edit form; the route carries the policy id. See Create.SaveClick. */
    SaveChanges(): void {
        const form = one(`#${EDIT_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const policyId = (one('#PolicyId') as HTMLInputElement | null)?.value ?? '';
        saving(true);
        void ajaxJson({
            url: `PersonPolicy/Edit/${policyId}`,
            method: 'PUT',
            data: serializeUtc(form),
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to save policy',
            onSuccess: () => {
                toast('success', 'Policy saved');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The FORM, not PersonPolicyEditMessageContainer: that div has
                // no div.validation-summary of its own, so every model-level
                // message was dropped in silence.
                if (modelState) setValidationModelState(modelState, `#${EDIT_FORM}`);
                toast('danger', 'Failed to save policy', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },
};

function removeRow(id: number | string): void {
    for (const row of all(`tr[data-id="${CSS.escape(String(id))}"]`)) row.remove();
}
